'use client';

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '@/utils/client';
import axios from 'axios';
import { config } from '@/config';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from '@/components/core/v2/toaster';

import type { Event, EventPriority } from './types';

// Add CalendarEventType interface
interface CalendarEventType {
  id: string;
  name: string;
  color: string;
}

function noop(): void {
  return undefined;
}

// createEvent/updateEvent are awaited by the dialog, so the placeholder must be
// thenable too.
function asyncNoop(): Promise<unknown> {
  return Promise.resolve();
}

interface EventCreateParams {
  title: string;
  description?: string;
  start: Date;
  end: Date;
  allDay: boolean;
  priority?: EventPriority;
  type?: string;
}

interface EventUpdateParams {
  title?: string;
  description?: string;
  start?: Date;
  end?: Date;
  allDay?: boolean;
  priority?: EventPriority;
  type?: string;
}

export interface CalendarContextValue {
  events: Map<string, Event>;
  eventTypes: CalendarEventType[];
  currentEventId?: string;
  setCurrentEventId: (eventId?: string) => void;
  // create/update resolve or REJECT so the dialog can stay open (and keep the
  // user's input) when the request fails.
  createEvent: (params: EventCreateParams) => Promise<unknown>;
  deleteEvent: (eventId: string) => void;
  updateEvent: (eventId: string, params: EventUpdateParams) => Promise<unknown>;
  isLoading: boolean;
  isError: boolean;
  isLoadingEventTypes: boolean;
}

export const CalendarContext = React.createContext<CalendarContextValue>({
  events: new Map(),
  eventTypes: [],
  setCurrentEventId: noop,
  createEvent: asyncNoop,
  deleteEvent: noop,
  updateEvent: asyncNoop,
  isLoading: false,
  isError: false,
  isLoadingEventTypes: false,
});

export interface CalendarProviderProps {
  children: React.ReactNode;
}

// API functions
const fetchEvents = async (): Promise<Event[]> => {
  const response = await axiosClient.get('/api/v2/calendar/calendar-events');
  const data = response.data;
  
  // Handle both resource format (array of objects) and direct data format
  const events = Array.isArray(data) ? data : data.data || [];
  
  return events.map((event: any) => ({
    id: event.id,
    title: event.title,
    description: event.description,
    start: new Date(event.start),
    end: new Date(event.end),
    allDay: event.allDay,
    priority: event.priority,
    type: event.type,
    color: event.color,
    visibility: event.visibility,
    isRecurring: event.isRecurring,
    recurrenceType: event.recurrenceType,
    recurrenceInterval: event.recurrenceInterval,
    recurrenceDays: event.recurrenceDays,
    recurrenceEndDate: event.recurrenceEndDate ? new Date(event.recurrenceEndDate) : undefined,
    recurrenceCount: event.recurrenceCount,
    parentEventId: event.parentEventId,
  }));
};

const fetchEventTypes = async (): Promise<CalendarEventType[]> => {
  const response = await axiosClient.get('/api/v2/calendar/calendar-events?getTypes=1');
  return response.data;
};

const createEventAPI = async (params: EventCreateParams): Promise<Event> => {
  await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
  const response = await axios.post(`${config.site.api}/api/v2/calendar/calendar-events`, {
    title: params.title,
    description: params.description,
    start_date: params.start.toISOString(),
    end_date: params.end.toISOString(),
    all_day: params.allDay,
    ...(params.type?.trim() ? { type: params.type.trim() } : {}),
    ...(params as any).visibility ? { visibility: (params as any).visibility } : {},
    ...(params as any).isRecurring ? {
      is_recurring: (params as any).isRecurring,
      recurrence_type: (params as any).recurrenceType,
      recurrence_interval: (params as any).recurrenceInterval,
      recurrence_days: (params as any).recurrenceDays,
      recurrence_end_date: (params as any).recurrenceEndDate?.toISOString(),
      recurrence_count: (params as any).recurrenceCount,
    } : {},
  }, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      Accept: 'application/json',
    },
  });
  
  const data = response.data.data || response.data;
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    start: new Date(data.start),
    end: new Date(data.end),
    allDay: data.allDay,
    priority: data.priority,
    type: data.type,
    color: data.color,
    visibility: data.visibility,
    isRecurring: data.isRecurring,
    recurrenceType: data.recurrenceType,
    recurrenceInterval: data.recurrenceInterval,
    recurrenceDays: data.recurrenceDays,
    recurrenceEndDate: data.recurrenceEndDate ? new Date(data.recurrenceEndDate) : undefined,
    recurrenceCount: data.recurrenceCount,
    parentEventId: data.parentEventId,
  };
};

const updateEventAPI = async (eventId: string, params: EventUpdateParams): Promise<Event> => {
  await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
  const response = await axios.patch(`${config.site.api}/api/v2/calendar/calendar-events/${eventId}`, {
    title: params.title,
    description: params.description,
    start_date: params.start?.toISOString(),
    end_date: params.end?.toISOString(),
    all_day: params.allDay,
    ...(params.type?.trim() ? { type: params.type.trim() } : {}),
    ...(params as any).visibility ? { visibility: (params as any).visibility } : {},
    ...(params as any).isRecurring ? {
      is_recurring: (params as any).isRecurring,
      recurrence_type: (params as any).recurrenceType,
      recurrence_interval: (params as any).recurrenceInterval,
      recurrence_days: (params as any).recurrenceDays,
      recurrence_end_date: (params as any).recurrenceEndDate?.toISOString(),
      recurrence_count: (params as any).recurrenceCount,
    } : {},
  }, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      Accept: 'application/json',
    },
  });
  
  const data = response.data.data || response.data;
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    start: new Date(data.start),
    end: new Date(data.end),
    allDay: data.allDay,
    priority: data.priority,
    type: data.type,
    color: data.color,
    visibility: data.visibility,
    isRecurring: data.isRecurring,
    recurrenceType: data.recurrenceType,
    recurrenceInterval: data.recurrenceInterval,
    recurrenceDays: data.recurrenceDays,
    recurrenceEndDate: data.recurrenceEndDate ? new Date(data.recurrenceEndDate) : undefined,
    recurrenceCount: data.recurrenceCount,
    parentEventId: data.parentEventId,
  };
};

const deleteEventAPI = async (eventId: string): Promise<void> => {
  await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
  await axios.delete(`${config.site.api}/api/v2/calendar/calendar-events/${eventId}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      Accept: 'application/json',
    },
  });
};

export function CalendarProvider({ children }: CalendarProviderProps): React.JSX.Element {
  const [currentEventId, setCurrentEventId] = React.useState<string>();
  const queryClient = useQueryClient();

  // Fetch events
  const { data: events = [], isLoading, isError } = useQuery({
    queryKey: ['calendar-events'],
    queryFn: fetchEvents,
  });

  // Fetch event types
  const { data: eventTypes = [], isLoading: isLoadingEventTypes } = useQuery({
    queryKey: ['calendar-event-types'],
    queryFn: fetchEventTypes,
  });

  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: createEventAPI,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    // Without this the request failed silently: the dialog closes on submit, so
    // a 422 ("The description field is required.") was never shown.
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to create event'));
    },
  });

  // Update event mutation
  const updateEventMutation = useMutation({
    mutationFn: ({ eventId, params }: { eventId: string; params: EventUpdateParams }) =>
      updateEventAPI(eventId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to update event'));
    },
  });

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: deleteEventAPI,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete event'));
    },
  });

  const handleCreateEvent = React.useCallback(
    (params: EventCreateParams) => createEventMutation.mutateAsync(params),
    [createEventMutation]
  );

  const handleUpdateEvent = React.useCallback(
    (eventId: string, params: EventUpdateParams) => updateEventMutation.mutateAsync({ eventId, params }),
    [updateEventMutation]
  );

  const handleDeleteEvent = React.useCallback(
    (eventId: string) => {
      deleteEventMutation.mutate(eventId);
    },
    [deleteEventMutation]
  );

  // Convert events array to Map (use string keys for consistent lookup with FullCalendar)
  const eventsMap = React.useMemo(() => {
    return new Map(events.map((event) => [String(event.id), event]));
  }, [events]);

  return (
    <CalendarContext.Provider
      value={{
        events: eventsMap,
        eventTypes,
        currentEventId,
        setCurrentEventId,
        createEvent: handleCreateEvent,
        deleteEvent: handleDeleteEvent,
        updateEvent: handleUpdateEvent,
        isLoading,
        isError,
        isLoadingEventTypes,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
}
